import type { Fundamentals, HistoricalSeries, PricePoint, YearValue } from "@/lib/data/types";
import type { ScoreResult } from "@/lib/scoring/types";
import { computeScore } from "@/lib/scoring/scoring";

export interface RetroactiveScorePoint {
  year: number;
  result: ScoreResult;
}

export interface DailyRetroactiveScorePoint {
  date: string;
  score: number;
}

const HISTORICAL_KEYS = [
  "pe",
  "pb",
  "dividendYield",
  "dividendPerShare",
  "grossMargin",
  "netMargin",
  "roic",
  "roe",
  "priceToSales",
  "revenue",
  "eps",
  "freeCashFlow",
  "sharesOutstanding",
  "netIncome",
  "totalDebt",
] as const satisfies readonly (keyof HistoricalSeries)[];

function upToYear(series: YearValue[], year: number): YearValue[] {
  return series.filter((v) => v.year <= year);
}

function valueInYear(series: YearValue[], year: number): number | undefined {
  return series.find((v) => v.year === year)?.value;
}

/**
 * Score-Zeitmaschine (backlog.md -> D3): rechnet den Erikonomie-Score rueckwirkend an
 * vergangenen Geschaeftsjahresenden neu durch - ruft dafuer dieselbe, bereits getestete
 * `computeScore()` auf (keine zweite Scoring-Logik). Vermeidet Look-Ahead-Bias, indem
 * JEDE Zeitreihe (Fundamentaldaten UND Kurshistorie) konsequent exakt am Stichjahr
 * abgeschnitten wird, bevor sie in `computeScore()` geht - nicht nur ein "letzte N
 * Jahre"-Fenster ab heute genommen wird.
 *
 * Eine bewusste, dokumentierte Vereinfachung (siehe backlog.md D3 + Punkt 16 fuer die
 * Herleitung), noetig, weil es dafuer keine ehrlich rekonstruierbare historische
 * Datenquelle im Projekt gibt: Net Debt/EBITDA (Bereich 5) bleibt fuer JEDES Jahr "nicht
 * verfuegbar" - `Fundamentals` haelt `netDebt`/`ebitda` nur als AKTUELLEN Wert, keine
 * historische YearValue-Reihe existiert dafuer. Lieber diesen einen Bereich konsequent
 * auslassen (`computeScore()` rechnet die uebrigen Bereiche anteilig auf 100 hoch, siehe
 * dortige Renormalisierungs-Logik) als einen erfundenen Wert einzusetzen.
 *
 * Jahre ganz ohne verfuegbaren Bereich werden ausgelassen statt eines bedeutungslosen
 * 0-Punkte-Ausreissers. Reine Funktion, kein I/O.
 */
export function computeRetroactiveScoreSeries(
  historical: HistoricalSeries,
  priceHistory: PricePoint[],
): RetroactiveScorePoint[] {
  const years = new Set<number>();
  for (const key of HISTORICAL_KEYS) {
    for (const v of historical[key]) years.add(v.year);
  }

  const points: RetroactiveScorePoint[] = [];
  for (const year of [...years].sort((a, b) => a - b)) {
    const slicedHistorical: HistoricalSeries = {
      pe: upToYear(historical.pe, year),
      pb: upToYear(historical.pb, year),
      dividendYield: upToYear(historical.dividendYield, year),
      dividendPerShare: upToYear(historical.dividendPerShare, year),
      grossMargin: upToYear(historical.grossMargin, year),
      netMargin: upToYear(historical.netMargin, year),
      roic: upToYear(historical.roic, year),
      roe: upToYear(historical.roe, year),
      operatingMargin: upToYear(historical.operatingMargin, year),
      roa: upToYear(historical.roa, year),
      priceToSales: upToYear(historical.priceToSales, year),
      revenue: upToYear(historical.revenue, year),
      eps: upToYear(historical.eps, year),
      freeCashFlow: upToYear(historical.freeCashFlow, year),
      sharesOutstanding: upToYear(historical.sharesOutstanding, year),
      netIncome: upToYear(historical.netIncome, year),
      totalDebt: upToYear(historical.totalDebt, year),
    };

    const fundamentals: Fundamentals = {
      netMargin: valueInYear(historical.netMargin, year),
      pe: valueInYear(historical.pe, year),
      freeCashflow: valueInYear(historical.freeCashFlow, year),
      revenue: valueInYear(historical.revenue, year),
      dividendYield: valueInYear(historical.dividendYield, year),
      // netDebt/ebitda bewusst nicht gesetzt, siehe Docstring oben.
    };

    const yearEnd = `${year}-12-31`;
    const slicedPriceHistory = priceHistory.filter((p) => p.date <= yearEnd);

    const result = computeScore({
      fundamentals,
      historical: slicedHistorical,
      priceHistory: slicedPriceHistory,
    });
    if (result.dataQuality === 0) continue;
    points.push({ year, result });
  }

  return points;
}

/**
 * Stuft die jaehrlichen Score-Zeitmaschine-Punkte auf beliebige (typischerweise taegliche
 * Kurs-)Stichtage fort - gleiches Stufen-Prinzip wie `computeDailyMultiple()`
 * (lib/valuation/multiples.ts), hier aber ohne Verhaeltnisbildung: der Score-WERT selbst
 * wird fortgeschrieben, nicht durch einen Kurs geteilt (eigene, bewusst nicht geteilte
 * Funktion, da die beiden Anwendungsfaelle keine gemeinsame Berechnung haben, nur dasselbe
 * Stufen-Muster). Stichtage vor dem ersten bekannten Score-Jahr werden ausgelassen statt
 * einen Wert zu erfinden. Reine Funktion, kein I/O.
 */
export function stepRetroactiveScoreOntoDates(
  points: RetroactiveScorePoint[],
  dates: string[],
): DailyRetroactiveScorePoint[] {
  const sortedPoints = [...points].sort((a, b) => a.year - b.year);
  const sortedDates = [...dates].sort((a, b) => a.localeCompare(b));
  const out: DailyRetroactiveScorePoint[] = [];
  let stepIndex = -1;

  for (const date of sortedDates) {
    while (
      stepIndex + 1 < sortedPoints.length &&
      `${sortedPoints[stepIndex + 1].year}-12-31` <= date
    ) {
      stepIndex++;
    }
    if (stepIndex < 0) continue;
    out.push({ date, score: sortedPoints[stepIndex].result.total });
  }
  return out;
}
